import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, ListTodo, ReceiptText, Plus, Upload, UploadCloud, UserPlus, FolderOpen } from "lucide-react";
import { doc, getDoc, addDoc, collection, updateDoc, arrayUnion, query, orderBy, onSnapshot } from "firebase/firestore";
import { ref as storageFileRef, uploadBytes, getDownloadURL } from "firebase/storage";
import toast from "react-hot-toast";
import { db, storage } from "../../../firebase";
import { addTimelineEvent } from "../../../services/timelineService";
import { PRIORITY_META } from "../../../pages/tasks/tasksShared";
import "./caseDetails.css";

const AVATAR_TINTS = [
  { bg: "#EEF2FF", fg: "#4F46E5" },
  { bg: "#ECFDF5", fg: "#059669" },
  { bg: "#FFF7ED", fg: "#D97706" },
  { bg: "#FDF2F8", fg: "#DB2777" },
];

const initialsOf = (n = "") =>
  n.trim().split(/\s+/).slice(0, 2).map((w) => w[0] || "").join("").toUpperCase() || "?";

// Tab contents for Documents / Invoices / Tasks on Case Details.
// Purely presentational — lists are fetched once in CaseDetails and passed in.

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

const TabShell = ({ title, action, children }) => (
  <div className="cd-panel">
    <div className="cd-ov-head">
      <h3 className="cd-panel-title" style={{ margin: 0 }}>{title}</h3>
      {action}
    </div>
    {children}
  </div>
);

// Documents tab — an AddCase-style drag & drop upload area (the document LIST
// lives in Overview → Important Documents). Uploads follow the same flow as
// the Case Files screen: Storage upload → `files` collection doc → case doc's
// legacy `files` array → timeline event.
export function CaseDocumentsTab({ caseId, caseNo, caseCollection, docs, uploaderUid, onUploaded }) {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (fileList) => {
    const filesArray = Array.from(fileList || []);
    if (!filesArray.length || uploading) return;

    const allowed = ["pdf", "jpg", "jpeg", "png", "heic"];
    const valid = filesArray.filter((f) =>
      allowed.includes((f.name.split(".").pop() || "").toLowerCase())
    );
    if (valid.length !== filesArray.length) {
      toast.error("Some files were skipped. Only PDF, JPG, JPEG, PNG and HEIC files are allowed.");
    }
    if (!valid.length) return;

    setUploading(true);
    const toastId = toast.loading(
      valid.length === 1 ? `Uploading ${valid[0].name}...` : `Uploading ${valid.length} files...`
    );
    try {
      const results = [];
      for (const file of valid) {
        const sRef = storageFileRef(storage, `case-file/${Date.now()}-${file.name}`);
        await uploadBytes(sRef, file);
        const url = await getDownloadURL(sRef);
        const fileDoc = {
          caseNo: caseNo || "",
          caseId,
          fileName: file.name,
          file_url: url,
          fileSize: file.size || 0,
          lawyer_uid: uploaderUid || "",
          uploadedAt: new Date().toISOString(),
        };
        const docRef = await addDoc(collection(db, "files"), fileDoc);
        results.push({ id: docRef.id, ...fileDoc });
      }

      // Keep the case document's legacy `files` array in sync + log timeline.
      if (caseCollection) {
        await updateDoc(doc(db, caseCollection, caseId), {
          files: arrayUnion(...results.map((r) => r.file_url)),
        }).catch(() => {});
        results.forEach((r) =>
          addTimelineEvent(caseId, caseCollection, {
            type: "file_added",
            title: "File uploaded",
            description: `File "${r.fileName}" was uploaded`,
            performedBy: uploaderUid || "",
            performedByName: "",
            changes: {
              "File Name": { old: "—", new: r.fileName },
              Action: { old: "—", new: "Uploaded" },
            },
          })
        );
      }

      toast.success(
        results.length === 1 ? `${results[0].fileName} uploaded` : `${results.length} files uploaded`,
        { id: toastId }
      );
      onUploaded?.(results);
    } catch (e) {
      console.error("Document upload error:", e);
      toast.error("Failed to upload files", { id: toastId });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <TabShell
      title={`Documents (${docs.length})`}
      action={
        <button
          className="cd-btn"
          onClick={() => navigate(`/portal/case/${caseId}/case-files`)}
          title="Open the full Case Files manager (folders, bookmarks, bulk move)"
        >
          <FolderOpen size={14} />
          Open Case Files
        </button>
      }
    >
      {/* Drag & drop / upload — same look as the Add Case upload area */}
      <div
        onClick={() => !uploading && fileInputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          if (e.dataTransfer?.files?.length) handleFiles(e.dataTransfer.files);
        }}
        className={`cursor-pointer rounded-xl border-2 border-dashed border-slate-200 bg-slate-50/70 px-6 py-8 text-center transition-colors hover:border-yellow-400 hover:bg-yellow-50/40 ${
          uploading ? "opacity-60 pointer-events-none" : ""
        }`}
      >
        <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-white shadow-sm">
          <UploadCloud className="h-5 w-5 text-yellow-600" />
        </div>
        <div className="flex flex-wrap items-center justify-center gap-2 text-sm text-slate-600">
          <span>{uploading ? "Uploading..." : "Drag and drop files here or"}</span>
          {!uploading && (
            <span className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[13px] font-semibold text-slate-700">
              <Upload className="h-3.5 w-3.5" /> Upload File
            </span>
          )}
        </div>
        <p className="mt-2 text-[12px] text-slate-400">
          Supported formats: PDF, JPG, JPEG, PNG, HEIC
        </p>
      </div>

      {docs.length > 0 && (
        <p className="mt-3 text-[13px] font-medium text-slate-600">
          {docs.length} document{docs.length > 1 ? "s" : ""} attached — see Overview → Important
          Documents.
        </p>
      )}

      <input
        type="file"
        ref={fileInputRef}
        onChange={(e) => handleFiles(e.target.files)}
        className="hidden"
        multiple
        accept=".pdf,.jpg,.jpeg,.png,.heic"
      />
    </TabShell>
  );
}

export function CaseInvoicesTab({ caseId, invoices }) {
  const navigate = useNavigate();
  const statusPill = (s) => {
    const v = (s || "raised").toLowerCase();
    if (v === "paid") return <span className="cd-pill cd-pill-green">Paid</span>;
    if (v === "draft") return <span className="cd-pill" style={{ background: "#EEF1F6", color: "#606A7B" }}>Draft</span>;
    return <span className="cd-pill cd-pill-amber">{v === "sent" ? "Sent" : "Pending"}</span>;
  };
  return (
    <TabShell
      title={`Invoices (${invoices.length})`}
      action={
        <button
          className="cd-btn"
          onClick={() => navigate(`/portal/create-invoice?case=${caseId}`)}
        >
          <Plus size={14} />
          Create Invoice
        </button>
      }
    >
      {invoices.length === 0 ? (
        <p className="cd-rail-muted">No invoices raised for this case yet</p>
      ) : (
        <div className="cd-ov-list">
          {invoices.map((inv) => (
            <button
              key={inv.id}
              className="cd-ov-item"
              onClick={() => navigate(`/portal/invoice/${inv.id}`)}
            >
              <span className="cd-ov-item-icon" style={{ background: "#EEF2FF", color: "#4F46E5" }}>
                <ReceiptText size={13} />
              </span>
              <span className="cd-ov-item-name" title={inv.invoiceNumber}>
                {inv.invoiceNumber || "Invoice"}
                {inv.contactName ? ` · ${inv.contactName}` : ""}
              </span>
              <span className="cd-ov-item-meta">
                {inv.currency === "USD" ? "$" : inv.currency === "EUR" ? "€" : "₹"}
                {Number(inv.totalAmount || 0).toLocaleString("en-IN")}
              </span>
              {statusPill(inv.status)}
            </button>
          ))}
        </div>
      )}
    </TabShell>
  );
}

export function CaseTasksTab({ caseId, tasks }) {
  const navigate = useNavigate();
  return (
    <TabShell
      title={`Tasks (${tasks.length})`}
      action={
        <button
          className="cd-btn"
          onClick={() => navigate(`/portal/add-task?caseId=${caseId}`)}
        >
          <Plus size={14} />
          Add Task
        </button>
      }
    >
      {tasks.length === 0 ? (
        <p className="cd-rail-muted">No tasks linked to this case</p>
      ) : (
        <div className="cd-ov-list">
          {tasks.map((t) => {
            const pm = PRIORITY_META[t.priority] || PRIORITY_META.medium;
            return (
              <button
                key={t.id}
                className="cd-ov-item"
                onClick={() => navigate(`/portal/task/${t.id}`)}
              >
                <span className="cd-ov-item-icon" style={{ background: "#F5F3FF", color: "#7C3AED" }}>
                  <ListTodo size={13} />
                </span>
                <span className="cd-ov-item-name" title={t.title}>
                  {t.title}
                </span>
                {t.dueDate && (
                  <span className="cd-ov-item-meta">{fmtDate(t.dueDate)}</span>
                )}
                <span className={`cd-ov-pill ring-1 ${pm.pill}`}>{pm.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </TabShell>
  );
}

// "Team" tab — members-with-access table per the mockup. Data = the case's
// resolved teamMembers; phone is looked up from each member's profile doc.
// There is no per-member access level in the data model, so Access shows
// Owner / Full Access.
export function CaseTeamTab({ caseData, onManageAccess }) {
  const members = Array.isArray(caseData?.teamMembers) ? caseData.teamMembers : [];
  const [phones, setPhones] = useState({});

  useEffect(() => {
    if (members.length === 0) return;
    (async () => {
      const out = {};
      await Promise.all(
        members.map(async (m) => {
          try {
            const snap = await getDoc(doc(db, m._isClient ? "client" : "lawyers", m.uid));
            if (snap.exists()) {
              const d = snap.data();
              out[m.uid] = d.mobileNumber || d.contact || d.phone || "";
            }
          } catch (e) {
            /* profile not readable — leave phone blank */
          }
        })
      );
      setPhones(out);
    })();
    // eslint-disable-next-line
  }, [caseData?.teamMembers]);

  return (
    <TabShell
      title={`Team Members (${members.length})`}
      action={
        onManageAccess && (
          <button className="cd-btn cd-btn-gold" onClick={onManageAccess}>
            <UserPlus size={14} />
            Add Member
          </button>
        )
      }
    >
      {members.length === 0 ? (
        <p className="cd-rail-muted">This case is not shared with anyone yet</p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr>
                <th className="text-left">Member</th>
                <th className="text-left">Role</th>
                <th className="text-left">Email</th>
                <th className="text-left">Phone</th>
                <th className="text-left">Access</th>
              </tr>
            </thead>
            <tbody>
              {members.map((m, i) => {
                const tint = AVATAR_TINTS[i % AVATAR_TINTS.length];
                const isOwnerRow = m.uid === caseData?.owner;
                return (
                  <tr key={m.uid} className="border-t">
                    <td>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                        <span
                          className="cd-party-avatar"
                          style={{ width: 30, height: 30, fontSize: 11, background: tint.bg, color: tint.fg }}
                        >
                          {initialsOf(m.name)}
                        </span>
                        <span style={{ fontWeight: 600 }}>{m.name}</span>
                      </span>
                    </td>
                    <td>{m._isClient ? "Client" : "Advocate"}</td>
                    <td>{m.email || "—"}</td>
                    <td>{phones[m.uid] || "—"}</td>
                    <td>
                      <span className={`cd-pill ${isOwnerRow ? "cd-pill-amber" : "cd-pill-green"}`}>
                        {isOwnerRow ? "Owner" : "Full Access"}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </TabShell>
  );
}

// Timestamp for comments / attachments — date + time.
const fmtStamp = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? String(iso)
    : d.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
};

const tintFor = (name = "") => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
  return AVATAR_TINTS[Math.abs(h) % AVATAR_TINTS.length];
};

// "Comments" tab — shared discussion on the case. Lives in a `comments`
// subcollection on the case doc, so the owner and everyone the case is shared
// with read/write the same thread; onSnapshot keeps it live for both sides.
export function CaseCommentsTab({ caseId, caseCollection, uid, userName }) {
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    if (!caseId || !caseCollection) return;
    const q = query(
      collection(db, caseCollection, caseId, "comments"),
      orderBy("createdAt", "desc")
    );
    const unsub = onSnapshot(
      q,
      (snap) => setComments(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (e) => console.error("Comments listener:", e)
    );
    return () => unsub();
  }, [caseId, caseCollection]);

  const addComment = async () => {
    const t = text.trim();
    if (!t || posting || !caseCollection) return;
    try {
      setPosting(true);
      await addDoc(collection(db, caseCollection, caseId, "comments"), {
        text: t,
        uid: uid || "",
        name: userName || "Unknown",
        createdAt: new Date().toISOString(),
      });
      setText("");
    } catch (e) {
      console.error("Add comment:", e);
      toast.error("Failed to add comment");
    } finally {
      setPosting(false);
    }
  };

  return (
    <TabShell title={`Comments (${comments.length})`}>
      {/* composer — single compact row, Enter posts */}
      <div className="mb-4 flex items-center gap-2 max-w-xl">
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && addComment()}
          placeholder="Write a comment… visible to everyone this case is shared with"
          className="flex-1 min-w-0 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-yellow-400/40"
        />
        <button
          className="rounded-lg bg-yellow-500 px-4 py-2 text-sm font-semibold text-gray-900 hover:bg-yellow-600 disabled:opacity-50 whitespace-nowrap"
          onClick={addComment}
          disabled={posting || !text.trim()}
        >
          {posting ? "Posting…" : "Comment"}
        </button>
      </div>

      {comments.length === 0 ? (
        <p className="cd-rail-muted">No comments yet — start the discussion.</p>
      ) : (
        <div className="cd-comment-list">
          {comments.map((c) => {
            const tint = tintFor(c.name);
            return (
              <div className="cd-comment" key={c.id}>
                <span className="cd-avatar" style={{ background: tint.bg, color: tint.fg }}>
                  {initialsOf(c.name)}
                </span>
                <div className="cd-comment-body">
                  <p className="cd-comment-meta">
                    <strong>{c.name || "Unknown"}</strong>
                    {c.uid === uid && <span className="cd-comment-you">You</span>}
                  </p>
                  <p className="cd-comment-text">{c.text}</p>
                  <p className="cd-comment-time">{fmtStamp(c.createdAt)}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </TabShell>
  );
}

// "Additional Attachments" tab (shared cases) — a shared drop-space where the
// owner AND the members the case is shared with can upload files. Stored in an
// `attachments` subcollection on the case doc with the uploader's name + time;
// onSnapshot keeps both sides in sync.
export function CaseAttachmentsTab({ caseId, caseCollection, uid, userName }) {
  const fileInputRef = useRef(null);
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!caseId || !caseCollection) return;
    const q = query(
      collection(db, caseCollection, caseId, "attachments"),
      orderBy("uploadedAt", "desc")
    );
    const unsub = onSnapshot(
      q,
      (snap) => setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (e) => console.error("Attachments listener:", e)
    );
    return () => unsub();
  }, [caseId, caseCollection]);

  const handleFiles = async (fileList) => {
    const filesArray = Array.from(fileList || []);
    if (!filesArray.length || uploading || !caseCollection) return;

    const allowed = ["pdf", "jpg", "jpeg", "png", "heic"];
    const valid = filesArray.filter((f) =>
      allowed.includes((f.name.split(".").pop() || "").toLowerCase())
    );
    if (valid.length !== filesArray.length) {
      toast.error("Some files were skipped. Only PDF, JPG, JPEG, PNG and HEIC files are allowed.");
    }
    if (!valid.length) return;

    setUploading(true);
    const toastId = toast.loading(
      valid.length === 1 ? `Uploading ${valid[0].name}...` : `Uploading ${valid.length} files...`
    );
    try {
      for (const file of valid) {
        const sRef = storageFileRef(storage, `case-attachments/${Date.now()}-${file.name}`);
        await uploadBytes(sRef, file);
        const url = await getDownloadURL(sRef);
        await addDoc(collection(db, caseCollection, caseId, "attachments"), {
          fileName: file.name,
          file_url: url,
          fileSize: file.size || 0,
          uploadedBy: uid || "",
          uploadedByName: userName || "Unknown",
          uploadedAt: new Date().toISOString(),
        });
        addTimelineEvent(caseId, caseCollection, {
          type: "file_added",
          title: "Attachment added",
          description: `Attachment "${file.name}" was uploaded by ${userName || "Unknown"}`,
          performedBy: uid || "",
          performedByName: userName || "",
          changes: {
            "File Name": { old: "—", new: file.name },
            Action: { old: "—", new: "Uploaded" },
          },
        });
      }
      toast.success(
        valid.length === 1 ? `${valid[0].name} uploaded` : `${valid.length} files uploaded`,
        { id: toastId }
      );
    } catch (e) {
      console.error("Attachment upload error:", e);
      toast.error("Failed to upload files", { id: toastId });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <TabShell title={`Additional Attachments (${items.length})`}>
      {/* Step 1 — what's already been shared, with uploader + timestamp */}
      {items.length === 0 ? (
        <p className="cd-rail-muted" style={{ marginBottom: 14 }}>
          No attachments yet — anyone this case is shared with can add files below.
        </p>
      ) : (
        <div className="cd-ov-list" style={{ marginBottom: 16 }}>
          {items.map((f) => (
            <a
              key={f.id}
              className="cd-ov-item"
              href={f.file_url || undefined}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="cd-ov-item-icon" style={{ background: "#FEF2F2", color: "#DC2626" }}>
                <FileText size={13} />
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span className="cd-ov-item-name" title={f.fileName} style={{ display: "block" }}>
                  {f.fileName || "Attachment"}
                </span>
                <span className="cd-ov-item-sub">
                  by {f.uploadedByName || "Unknown"}
                  {f.uploadedBy === uid ? " (You)" : ""} · {fmtStamp(f.uploadedAt)}
                </span>
              </span>
            </a>
          ))}
        </div>
      )}

      {/* Upload area — same UI as the Documents tab / Add Case */}
      <div
        onClick={() => !uploading && fileInputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          if (e.dataTransfer?.files?.length) handleFiles(e.dataTransfer.files);
        }}
        className={`cursor-pointer rounded-xl border-2 border-dashed border-slate-200 bg-slate-50/70 px-6 py-8 text-center transition-colors hover:border-yellow-400 hover:bg-yellow-50/40 ${
          uploading ? "opacity-60 pointer-events-none" : ""
        }`}
      >
        <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-white shadow-sm">
          <UploadCloud className="h-5 w-5 text-yellow-600" />
        </div>
        <div className="flex flex-wrap items-center justify-center gap-2 text-sm text-slate-600">
          <span>{uploading ? "Uploading..." : "Drag and drop files here or"}</span>
          {!uploading && (
            <span className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[13px] font-semibold text-slate-700">
              <Upload className="h-3.5 w-3.5" /> Upload File
            </span>
          )}
        </div>
        <p className="mt-2 text-[12px] text-slate-400">
          Supported formats: PDF, JPG, JPEG, PNG, HEIC
        </p>
      </div>

      <input
        type="file"
        ref={fileInputRef}
        onChange={(e) => handleFiles(e.target.files)}
        className="hidden"
        multiple
        accept=".pdf,.jpg,.jpeg,.png,.heic"
      />
    </TabShell>
  );
}
